import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { fetchDatabase } from './api';
import { FilterChips } from './FilterChips';
import { formatBytes, formatCount, formatMs, formatPct, formatWhen } from './formatHealth';
import { HealthAreaChart } from './HealthAreaChart';
import { HealthError, HealthPageHeader, HealthSkeleton } from './HealthPageHeader';
import type { TimeWindow } from './types';
import { useLivePoll } from './useLivePoll';

const WINDOW_OPTIONS: { id: TimeWindow; label: string }[] = [
  { id: '1h', label: '1h' },
  { id: '6h', label: '6h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
];

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--muted)]/40 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">{label}</p>
      <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-[var(--foreground)]">{value}</p>
    </div>
  );
}

export const DatabaseHealthOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [windowKey, setWindowKey] = useState<TimeWindow>('1h');

  const onUnauth = useCallback(() => {
    sessionStorage.removeItem('portal_token');
    sessionStorage.removeItem('portal_user');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  const fetcher = useCallback((signal: AbortSignal) => fetchDatabase(windowKey, signal), [windowKey]);
  const { data, loading, refreshing, error, reload } = useLivePoll({
    intervalMs: 15_000,
    fetcher,
    onUnauthenticated: onUnauth,
    resetKey: windowKey,
  });

  if (loading && !data) return <HealthSkeleton />;
  if (error && !data) return <HealthError message={error} onRetry={() => reload()} />;
  if (!data) return null;

  const db = data.database;
  const b = db.backends || { active: 0, idle: 0, idleInTransaction: 0, waiting: 0, total: 0 };

  return (
    <div className="space-y-6">
      <Link
        to="/admin/system"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--mymoolah-blue)] hover:underline"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Platform Health
      </Link>

      <HealthPageHeader
        title="Database"
        subtitle="Cloud SQL System Insights for this environment."
        status={data.overall.status}
        reason={data.overall.reason}
        environment={data.environment}
        region={db.connectionName.split(':')[1] || 'africa-south1'}
        instanceId={db.instanceId}
        generatedAt={data.generatedAt}
        live={!document.hidden}
        refreshing={refreshing}
        onRefresh={() => reload(true)}
        consoleUrl={db.consoleUrl}
      />

      <FilterChips label="Window" value={windowKey} options={WINDOW_OPTIONS} onChange={setWindowKey} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Fact label="State" value={db.state || '—'} />
        <Fact label="Tier" value={db.tier || '—'} />
        <Fact label="HA" value={db.availabilityType || '—'} />
        <Fact label="Engine" value={db.databaseVersion || '—'} />
        <Fact label="Query latency" value={formatMs(db.dbLatencyMs)} />
        <Fact label="Cache hit" value={formatPct(db.cacheHitPercent, 1)} />
        <Fact label="Database size" value={formatBytes(db.databaseSizeBytes)} />
        <Fact label="Disk quota" value={db.dataDiskSizeGb != null ? `${db.dataDiskSizeGb} GB` : '—'} />
        <Fact label="Last backup" value={formatWhen(db.lastBackupAt)} />
        <Fact label="Backup status" value={db.lastBackupStatus || '—'} />
        <Fact label="Deadlocks" value={formatCount(db.deadlocks)} />
        <Fact
          label="Backends"
          value={`${formatCount(b.active)} active · ${formatCount(b.idle)} idle · ${formatCount(b.waiting)} waiting`}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">CPU</h2>
          <HealthAreaChart points={data.series.cpu || []} unit="percent" windowKey={windowKey} color="#86BE41" />
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">Memory</h2>
          <HealthAreaChart points={data.series.memory || []} unit="percent" windowKey={windowKey} />
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">Disk</h2>
          <HealthAreaChart points={data.series.disk || []} unit="percent" windowKey={windowKey} />
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">Connections</h2>
          <HealthAreaChart points={data.series.connections || []} unit="count" windowKey={windowKey} color="#86BE41" />
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">Read IOPS</h2>
          <HealthAreaChart points={data.series.readOps || []} unit="rate" windowKey={windowKey} />
        </div>
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-[var(--portal-shadow)]">
          <h2 className="mb-3 text-sm font-semibold text-[var(--foreground)]">Write IOPS</h2>
          <HealthAreaChart points={data.series.writeOps || []} unit="rate" windowKey={windowKey} />
        </div>
      </div>
    </div>
  );
};
